import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  IconButton,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SideBar from "../../components/SideBar";
import Header from "../../components/Header";
import { useUserContext } from "../../context/UserContext";
function Members() {
  const loginUserData = useUserContext();
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const [members, setMembers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    address: "",
    profile: null,
    profilePreview: "",
  });

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/User/get_members`);
      const result = await response.json();

      if (result.status === "Success") {
        const formattedMembers = result.items.map((member) => ({
          ...member,
          profilePreview: member.profile_image_url || "",
        }));
        setMembers(formattedMembers);
      } else {
        alert(result.status_message || "Failed to load members");
      }
    } catch (error) {
      console.error("Error fetching members:", error);
      alert("Error loading members");
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleOpenDialog = () => setOpenDialog(true);

  const handleCloseDialog = () => {
    setFormData({
      name: "",
      email: "",
      mobile: "",
      address: "",
      profile: null,
      profilePreview: "",
    });
    setEditingMemberId(null);
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        profile: file,
        profilePreview: preview,
      }));
    }
  };

  const handleEdit = (member) => {
    setFormData({
      name: member.name,
      email: member.email,
      mobile: member.mobile,
      address: member.address,
      profile: null,
      profilePreview: member.profilePreview,
    });
    setEditingMemberId(member.id);
    setOpenDialog(true);
  };

  const handleSaveMember = async () => {
    try {
      const payload = {
        id: editingMemberId,
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        address: formData.address,
        created_by: loginUserData.user, // 👈 Add this
        updated_by: loginUserData.user, // 👈 Add this
      };

      const sendRequest = async (finalPayload) => {
        const response = await fetch(`${API_BASE_URL}/User/add_member`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(finalPayload),
        });

        const result = await response.json();
        if (result.status === "Success") {
          alert(editingMemberId ? "Member updated" : "Member added");
          handleCloseDialog();
          fetchMembers();
        } else {
          alert(result.status_message || "Failed to save member");
        }
      };

      if (formData.profile) {
        const reader = new FileReader();
        reader.readAsDataURL(formData.profile);
        reader.onloadend = () => {
          payload.profile_image = reader.result;
          sendRequest(payload);
        };
      } else {
        sendRequest(payload);
      }
    } catch (error) {
      console.error("Save member error:", error);
      alert("Error saving member.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;

    try {
      const response = await fetch(`${API_BASE_URL}/User/delete_member/${id}`, {
        method: "POST",
      });

      const result = await response.json();
      if (result.status === "Success") {
        alert("Member deleted successfully");
        fetchMembers(); // Refresh list
      } else {
        alert(result.status_message || "Failed to delete member");
      }
    } catch (error) {
      console.error("Delete member error:", error);
      alert("Error deleting member.");
    }
  };

  return (
    <>
      <SideBar />
      <Header />
      <div className="MainContainer">
        <Box p={3}>
          <Typography variant="h5" mb={2}>
            Members
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ mb: 2 }}
          >
            Add Member
          </Button>

          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Profile</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Avatar src={member.profilePreview} alt={member.name} />
                    </TableCell>
                    <TableCell>{member.name}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>{member.mobile}</TableCell>
                    <TableCell>{member.address}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(member)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(member.id)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No members found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Dialog open={openDialog} onClose={handleCloseDialog}>
            <DialogTitle>
              {editingMemberId ? "Edit Member" : "Add Member"}
            </DialogTitle>
            <DialogContent>
              <TextField
                margin="dense"
                label="Name"
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Email"
                name="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Mobile"
                name="mobile"
                fullWidth
                value={formData.mobile}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Address"
                name="address"
                fullWidth
                value={formData.address}
                onChange={handleChange}
              />
              <Box mt={2}>
                <Typography variant="body2">Profile Image</Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {formData.profilePreview && (
                  <Box mt={1}>
                    <Avatar
                      src={formData.profilePreview}
                      alt="Profile Preview"
                      sx={{ width: 56, height: 56 }}
                    />
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSaveMember} variant="contained">
                {editingMemberId ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </div>
    </>
  );
}

export default Members;
